import type { ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import type { ArchiveSubmenuConfig } from '#/widgets/detail-toolbar'

import { canArchiveShipment } from '../lib/archive-eligibility'
import { ORDER_DISPATCH_ROUTE, STAGE_CONVERT_ROUTE } from '../lib/stage-route'
import type { StageRoute } from '../lib/stage-route'
import { useMarkShipmentStatus } from '../lib/use-mark-shipment-status'
import { SHIPMENT_STAGES } from '../model/stages'
import type { ShipmentStage } from '../model/stages'
import { ShipmentPrimaryAction } from './shipment-primary-action'

export interface ShipmentToolbarActionsOptions {
  stage: ShipmentStage
  shipment: ShipmentDetail | undefined
  /** Known shipment id (route/preview param) — available before the detail loads. */
  shipmentId?: string
  /**
   * Raw status from the list row, used to render the primary action and archive
   * menu optimistically while the detail fetch is in flight. Posted orders are
   * deliberately excluded from the optimistic primary action — their dispatch
   * buttons read `dispatches` (absent from the list response) and would target
   * the wrong loadboard until the detail resolves.
   */
  optimisticStatus?: ShipmentStatus
  /** Compact (sidebar) presentation — enables the mark-status quick button. */
  compact?: boolean
  /** Route-level close. Feeds the post/remove fallback and is the default onArchived. */
  onClose?: () => void
  /** Route-level "posted" handler (full page navigates to the posted tab). */
  onPosted?: () => void
  /** Override for the archive submenu callback. Defaults to onClose. */
  onArchived?: () => void
}

export interface ShipmentToolbarActions {
  primaryAction: ReactNode
  archive: ArchiveSubmenuConfig | undefined
}

/** Convert/dispatch navigation handlers, keyed off the known id so they work optimistically. */
function useNavHandlers(stage: ShipmentStage, shipmentId: string | undefined) {
  const navigate = useNavigate()
  const goTo = (route: StageRoute) =>
    void navigate({
      to: route.to as never,
      params: { [route.paramKey]: shipmentId } as never,
    })
  const convertRoute = STAGE_CONVERT_ROUTE[stage]
  return {
    onConvert:
      convertRoute && shipmentId ? () => goTo(convertRoute) : undefined,
    onDispatch:
      stage === SHIPMENT_STAGES.ORDER && shipmentId
        ? () => goTo(ORDER_DISPATCH_ROUTE)
        : undefined,
  }
}

/**
 * Resolves which shipment object each path renders from. The real detail wins;
 * otherwise a lightweight id+status stub stands in while it loads — except the
 * posted-order primary action, which needs `dispatches` and waits for the fetch.
 */
function resolveToolbarShipments(
  shipment: ShipmentDetail | undefined,
  shipmentId: string | undefined,
  optimisticStatus: ShipmentStatus | undefined,
): {
  primary: ShipmentDetail | undefined
  archiveSource: ShipmentDetail | undefined
} {
  if (shipment) return { primary: shipment, archiveSource: shipment }
  if (!shipmentId || !optimisticStatus) {
    return { primary: undefined, archiveSource: undefined }
  }
  const stub = { id: shipmentId, status: optimisticStatus } as ShipmentDetail
  return {
    primary: optimisticStatus === ShipmentStatus.posted ? undefined : stub,
    archiveSource: stub,
  }
}

/**
 * Single source of truth for the stage/status-driven detail-toolbar actions.
 * Builds the primary action element and the archive submenu config so the full
 * detail page and the sidebar share identical logic and only differ by props.
 */
export function useShipmentToolbarActions({
  stage,
  shipment,
  shipmentId,
  optimisticStatus,
  compact,
  onClose,
  onPosted,
  onArchived,
}: ShipmentToolbarActionsOptions): ShipmentToolbarActions {
  const { t } = useTranslation()
  const { markAs } = useMarkShipmentStatus()

  const resolvedId = shipment?.id ?? shipmentId
  const { onConvert, onDispatch } = useNavHandlers(stage, resolvedId)
  const { primary, archiveSource } = resolveToolbarShipments(
    shipment,
    resolvedId,
    optimisticStatus,
  )

  const primaryAction = (
    <ShipmentPrimaryAction
      stage={stage}
      shipment={primary}
      compact={compact}
      onConvert={onConvert}
      onDispatch={onDispatch}
      onMarkStatus={
        resolvedId ? (status) => markAs(resolvedId, status) : undefined
      }
      onClose={onClose}
      onPosted={onPosted}
    />
  )

  const archive = canArchiveShipment(archiveSource)
    ? {
        label: t('detailToolbar.archive'),
        stage,
        shipmentId: resolvedId,
        onArchived: onArchived ?? onClose,
      }
    : undefined

  return { primaryAction, archive }
}
